import { conversion } from 'country-configuration/currency/conversion'
import type { Prices } from 'product/types/Prices'
import type { CountryConfiguration } from 'types/countryConfiguration'
import { Channel } from 'types/masterData'
import { createStore } from 'zustand/vanilla'
import type { ExtraPriceProps, PriceClasses } from '../../types'
import { getPriceClasses } from '../../utils/getPriceClasses/getPriceClasses'

export type Price = {
	amount: number
	formatted: string
	additionalPrices: string[]
}

export type PricesState = {
	price: Price
	crossedOutPrices?: Price[]
	allCrossedOutWithoutLowest?: Price[]
	discountRate?: number
	hideSaleOrPromoPrice: boolean
	country: CountryConfiguration
	channel: Channel
	priceRef?: React.RefObject<HTMLDivElement | null>
	priceClasses: PriceClasses
	showDiscountRate?: boolean
} & Omit<
	ExtraPriceProps,
	'classes' | 'showOmnibusPrice' | 'dataTestId' | 'hidePriceHistory'
>
type PricesActions = {
	updatePrices: (prices: Prices) => void
}

export type PricesStore = PricesState & PricesActions

export const defaultInitState: PricesState = {
	price: {
		amount: 0,
		formatted: '',
		additionalPrices: [],
	},
	hideSaleOrPromoPrice: true,
	priceClasses: getPriceClasses(),
	country: {} as CountryConfiguration,
	channel: Channel.Shop,
}

export const createPricesStore = (initState: PricesState = defaultInitState) =>
	createStore<PricesStore>()((set) => ({
		horizontal: false,
		reverse: false,
		align: 'start',
		additionalPriceVertical: false,
		hasMicrodata: false,
		hidePriceHistory: false,
		showDiscountInfo: false,
		showAdditionalCurrencies: true,
		showDiscountRate: true,
		...initState,
		updatePrices: (prices: Prices) => {
			set((state) => {
				const { price: priceWithCurrency, additionalPrices } = conversion(
					prices.price,
					state.country,
					undefined,
					state.showAdditionalCurrencies
				)

				return {
					...state,
					price: {
						amount: prices.price,
						formatted: priceWithCurrency,
						additionalPrices: additionalPrices ?? [],
					},
					crossedOutPrices: getCrossedOutPrices({
						crossedPrices: prices.crossedOutPrices,
						country: state.country,
					}),
					allCrossedOutWithoutLowest: getCrossedOutPrices({
						crossedPrices: prices.allCrossedOutWithoutLowest,
						country: state.country,
					}),
					discountRate: prices.discountRate,
				}
			})
		},
	}))

const getCrossedOutPrices = ({
	crossedPrices,
	country,
}: {
	crossedPrices?: number[]
	country: CountryConfiguration
}) =>
	crossedPrices?.map((price) => {
		const {
			price: allCrossedOutWithoutLowestPriceWithCurrency,
			additionalPrices: allCrossedOutWithoutLowestAdditionalPrices,
		} = conversion(price, country, undefined, true)

		return {
			amount: price,
			formatted: allCrossedOutWithoutLowestPriceWithCurrency,
			additionalPrices: allCrossedOutWithoutLowestAdditionalPrices ?? [],
		}
	})
